<!-- @format -->

@let bemRoot = "app-horizontal-timeline";
@let timeline = timelineData();
<!-- tabindex makes the scrollable region reachable and scrollable with the keyboard -->
<section [class]="bemRoot" appDragToScroll tabindex="0" [attr.aria-labelledby]="labelledBy()" *transloco="let t">
  <ul class="{{ bemRoot }}__track">
    @for (term of timeline.studyTerms; track term.valid.startDate) {
      <li class="{{ bemRoot }}__track__study-term">
        <div class="{{ bemRoot }}__track__study-term__heading" [style]="gridAreaStyle(term)">
          <div class="{{ bemRoot }}__track__study-term__marker"></div>
          <fudis-heading [level]="studyTermHeadingLevel()" [variant]="'lg'" class="text-capitalize">
            {{ term.startYear }} – {{ term.name | localizedString }}
          </fudis-heading>
          <fudis-body-text [variant]="'lg-light'" class="fudis-mb-sm">
            {{ t("TIMELINE.STUDY_TERM.SUBHEADING", { valid: term.valid | localDateRange }) }}
          </fudis-body-text>
          <fudis-dl [variant]="'compact'" class="fudis-mb-md">
            <fudis-dl-item>
              <fudis-dt [contentText]="t('TIMELINE.TIMED_CREDITS')" />
              <fudis-dd [contentText]="term.timedCredits | creditRange: 'TEXT' : true" />
            </fudis-dl-item>
            <fudis-dl-item>
              <fudis-dt [contentText]="t('TIMELINE.ATTAINED_CREDITS')" />
              <fudis-dd [contentText]="term.attainedCredits | decimalNumber" />
            </fudis-dl-item>
          </fudis-dl>
        </div>
        @if (timeline.studyRightExpiredInfo?.columnStart === term.columnStart) {
          @let studyRightExpiredInfo = timeline.studyRightExpiredInfo;
          <fudis-notification class="fudis-mb-sm" [style]="gridAreaStyle(studyRightExpiredInfo)">
            <fudis-body-text>
              {{
                t("TIMELINE.STUDY_RIGHT_EXPIRED_NOTIFICATION", {
                  studyRightName: studyRightExpiredInfo.studyRightId | studyRightName | async | localizedString,
                  studyRightExpiredDate: studyRightExpiredInfo.studyRightExpiredDateInclusive | localDateFormat,
                })
              }}
            </fudis-body-text>
          </fudis-notification>
        }
        <!-- The study periods of all study terms are in one list, so the ones of this study term are picked by their columns -->
        @for (period of timeline.studyPeriods; track period.locator) {
          @if (period.columnStart >= term.columnStart && period.columnEnd <= term.columnEnd) {
            <div class="{{ bemRoot }}__track__study-period" [style]="gridAreaStyle(period)">
              <fudis-heading [level]="studyPeriodHeadingLevel()" [variant]="'md'" class="text-capitalize fudis-mb-none">{{
                period.name | localizedString
              }}</fudis-heading>
              <fudis-body-text [variant]="'md-light'" class="fudis-mb-xs">
                {{ t("TIMELINE.STUDY_PERIOD.SUBHEADING", { valid: period.valid | localDateRange }) }}
              </fudis-body-text>

              <fudis-dl>
                <fudis-dl-item>
                  <fudis-dt [contentText]="t('TIMELINE.CREDITS')" />
                  <fudis-dd>
                    <fudis-dl [variant]="'compact'">
                      <fudis-dl-item>
                        <fudis-dt [contentText]="t('TIMELINE.TIMED')" />
                        <fudis-dd [contentText]="period.timedCredits | creditRange: 'TEXT' : true" />
                      </fudis-dl-item>
                      <fudis-dl-item>
                        <fudis-dt [contentText]="t('TIMELINE.ATTAINED')" />
                        <fudis-dd [contentText]="period.attainedCredits | decimalNumber" />
                      </fudis-dl-item>
                    </fudis-dl>
                  </fudis-dd>
                </fudis-dl-item>
              </fudis-dl>

              <fudis-button
                [variant]="'primary'"
                [size]="'small'"
                [label]="t('TIMELINE.SELECT_COURSE_UNITS.DIALOG_OPEN_BUTTON_EDIT')"
                [attr.aria-label]="`${t('TIMELINE.SELECT_COURSE_UNITS.DIALOG_OPEN_BUTTON_EDIT')} ${period.name | localizedString} ${period.valid | localDateRange}`"
                (handleClick)="openSelectCourseUnitsModal(period)" />

              @if (!period.hasTimedStudies) {
                <div class="{{ bemRoot }}__track__study-period__empty-state">
                  <fudis-body-text class="fudis-text-emptystate">
                    <i>{{ t("TIMELINE.STUDY_PERIOD.NO_TIMED_UNITS") }}</i>
                  </fudis-body-text>
                </div>
              }
            </div>

            @let studyPeriodTimelineItems = timelineItemsByStudyPeriod().get(period.locator);
            @if (studyPeriodTimelineItems?.length > 0) {
              <ul class="{{ bemRoot }}__track__timeline-items">
                @for (studyPeriodTimelineItem of studyPeriodTimelineItems; track studyPeriodTimelineItem.item.id) {
                  @let item = studyPeriodTimelineItem.item;
                  @if (!studyPeriodTimelineItem.isContinuee) {
                    <li
                      class="{{ bemRoot }}__track__timeline-item"
                      [style]="gridAreaStyle(item)"
                      [attr.data-timeline-item-key]="timelineItemKey(item)">
                      @if (isTimelineCourseUnit(item)) {
                        <app-timeline-course-unit [courseUnit]="item.courseUnit" [headingLevel]="studyPeriodItemHeadingLevel()" />
                      } @else if (isTimelineCustomCourseUnitAttainment(item)) {
                        <app-timeline-custom-course-unit-attainment
                          [customCourseUnitAttainment]="item.customCourseUnitAttainment"
                          [headingLevel]="studyPeriodItemHeadingLevel()" />
                      } @else if (isTimelineCustomStudyDraft(item)) {
                        <app-timeline-custom-study-draft
                          [customStudyDraft]="item.customStudyDraft"
                          [headingLevel]="studyPeriodItemHeadingLevel()" />
                      }
                    </li>
                  } @else {
                    <li
                      class="{{ bemRoot }}__track__timeline-item fudis-visually-hidden"
                      [style]="gridAreaStyle(item)"
                      (focusin)="showFocusOnVisibleTimelineItem($event, item)"
                      (focusout)="hideFocusOnVisibleTimelineItem()">
                      @if (isTimelineCourseUnit(item)) {
                        <app-timeline-course-unit [courseUnit]="item.courseUnit" [headingLevel]="studyPeriodItemHeadingLevel()" />
                      } @else if (isTimelineCustomCourseUnitAttainment(item)) {
                        <app-timeline-custom-course-unit-attainment
                          [customCourseUnitAttainment]="item.customCourseUnitAttainment"
                          [headingLevel]="studyPeriodItemHeadingLevel()" />
                      } @else if (isTimelineCustomStudyDraft(item)) {
                        <app-timeline-custom-study-draft
                          [customStudyDraft]="item.customStudyDraft"
                          [headingLevel]="studyPeriodItemHeadingLevel()" />
                      }
                    </li>
                  }
                }
              </ul>
            }
          }
        }
      </li>
    }
  </ul>
</section>
